import PropTypes from 'prop-types';
import React, { useCallback, useContext, useMemo } from 'react';
import { isRootNode, ROOT_NODE_ID } from '../../../dataViews/DynamicTree';
import makeTreePlugins from '../../../dataViews/DynamicTree/internal/makeTreePlugins';

import initializeTree from '../../../dataViews/NextTree/store/actionHandlers/initializeTree';
import Scroll from '../../../other/Scroll';
import Error from '../../../utils/Error';
import orderBy from '../../../../../common/utils/orderBy';
import Spinner from '../../../utils/Spinner';
import TranslationContext from '../../../utils/Translations/TranslationContext';
import styles from './TreeSelectorTooltipContent.scss';
import TreeSelectorTooltipContentItem from './TreeSelectorTooltipContentItem';

const treeId = 'treeSelector';

const TreeSelectorTooltipContent = ({
  adapter: {
    resolveNodeParentId,
    resolveNodeId,
    resolveNodeTitle,
    nodeIsLeaf,
    resolveNodeClassName,
  },
  plugins,
  selection,
  models,
  loading,
  error,
  isRenderOnlySelected,
  resolveIsNodeSelected,
  isFormField,
  hasSyntheticRootNode,
}) => {
  const { direction, t } = useContext(TranslationContext);

  const _resolveNodeParentId = useCallback(
    node => {
      if (hasSyntheticRootNode && isRootNode(node)) {
        return null;
      }

      const resolvedParentId = resolveNodeParentId
        ? resolveNodeParentId(node)
        : node.parentId;

      if (
        hasSyntheticRootNode &&
        (!resolvedParentId || resolvedParentId === ROOT_NODE_ID)
      ) {
        return ROOT_NODE_ID;
      }

      return resolvedParentId;
    },
    [hasSyntheticRootNode, resolveNodeParentId],
  );

  const _resolveNodeId = useCallback(
    node => {
      if (isRootNode(node)) {
        return ROOT_NODE_ID;
      }

      return resolveNodeId ? resolveNodeId(node) : node.id;
    },
    [resolveNodeId],
  );

  const _resolveNodeTitle = useCallback(
    node => (resolveNodeTitle ? resolveNodeTitle(node) : node?.name),
    [resolveNodeTitle],
  );

  const _nodeIsLeaf = useCallback(
    node => (nodeIsLeaf ? nodeIsLeaf(node) : node?.name),
    [nodeIsLeaf],
  );

  const treeOptions = useMemo(
    () => ({
      treeId,
      models,
      selection,
      adapter: {
        nodeParentId: _resolveNodeParentId,
        nodeId: _resolveNodeId,
        nodeTitle: _resolveNodeTitle,
        nodeIsLeaf: _nodeIsLeaf,
      },
      parents: {},
      plugins: plugins ? makeTreePlugins(plugins) : [],
    }),
    [
      _nodeIsLeaf,
      _resolveNodeId,
      _resolveNodeParentId,
      _resolveNodeTitle,
      models,
      selection,
      plugins,
    ],
  );

  const prepareTree = useCallback(
    () => initializeTree({}, treeOptions)[treeId],
    [treeOptions],
  );

  const filterNonSelected = useCallback((selectedIds, tree) => {
    let modelsInUse = {};

    selectedIds.forEach(selected => {
      getParents(selected, tree.parents, modelsInUse);
    });

    modelsInUse = [...Object.keys(modelsInUse), ...Array.from(selectedIds)];

    tree.models = modelsInUse.reduce((models, value) => {
      models[value] = tree.models[value];
      return models;
    }, {});

    return tree;
  }, []);

  const selectedIds = useMemo(
    () =>
      Array.isArray(selection)
        ? new Set(selection.map(node => _resolveNodeId(node)))
        : new Set([_resolveNodeId(selection)]),
    [_resolveNodeId, selection],
  );

  const preparedTree = useMemo(
    () =>
      isRenderOnlySelected || isFormField
        ? filterNonSelected(selectedIds, prepareTree())
        : prepareTree(),
    [
      filterNonSelected,
      isFormField,
      isRenderOnlySelected,
      prepareTree,
      selectedIds,
    ],
  );

  const rootNodes = useMemo(() => {
    const nodes = preparedTree.children.null || [];

    return orderBy(nodes, [
      item => preparedTree.models[item]?.sequence,
      item => _resolveNodeTitle(preparedTree.models[item]),
    ]);
  }, [preparedTree, _resolveNodeTitle]);

  if (loading || !models) {
    return <Spinner inline={false} title={t('Loading')} />;
  } else if (error) {
    return <Error error={error} />;
  } else if (!models) {
    return null;
  }

  return (
    <div data-layout-direction={direction} dir={direction}>
      <Scroll
        autoHeightMax={250}
        className={styles.rootList}
        classNameInner={styles.list}
        passProps={{
          'data-layout-direction': direction,
          dir: direction,
        }}
        tagName="ul"
      >
        {rootNodes.map(rootId => (
          <TreeSelectorTooltipContentItem
            key={rootId}
            id={rootId}
            isFormField={isFormField}
            resolveIsNodeSelected={resolveIsNodeSelected}
            resolveNodeClassName={resolveNodeClassName}
            resolveNodeTitle={resolveNodeTitle}
            selectedIds={selectedIds}
            t={t}
            tree={preparedTree}
          />
        ))}
      </Scroll>
    </div>
  );
};

TreeSelectorTooltipContent.propTypes = {
  models: PropTypes.array,
  loading: PropTypes.bool.isRequired,
  selection: PropTypes.oneOfType([
    PropTypes.any,
    PropTypes.arrayOf(PropTypes.any),
  ]).isRequired,
  isRenderOnlySelected: PropTypes.bool,
  adapter: PropTypes.shape({
    resolveNodeParentId: PropTypes.func,
    resolveNodeId: PropTypes.func,
    resolveNodeTitle: PropTypes.func,
    nodeIsLeaf: PropTypes.func,
    resolveNodeClassName: PropTypes.func,
  }),
  error: PropTypes.object,
  isFormField: PropTypes.bool,
  resolveIsNodeSelected: PropTypes.func,
  hasSyntheticRootNode: PropTypes.bool,
};

TreeSelectorTooltipContent.defaultProps = {
  error: null,
  isRenderOnlySelected: false,
  isFormField: false,
  adapter: {},
  resolveIsNodeSelected: null,
  models: [],
  hasSyntheticRootNode: false,
};

export default TreeSelectorTooltipContent;

function getParents(id, parents, usedParents) {
  if (!id) {
    return;
  }

  const parent = parents[id];

  if (!parent) {
    return;
  }

  usedParents[parent] = true;

  getParents(parent, parents, usedParents);
}
